import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

export type PeriodeJenis = "bulanan" | "tahunan";

export interface SirsPeriodeFilter {
  periode_jenis: PeriodeJenis;
  periode_tahun: number;
  periode_bulan?: number | null;
  total_tt?: number;
}

export interface RlFormMeta {
  rl: string;
  nama: string;
  periode: string;
}

export interface RlFormFieldView {
  path: string | null;
  no: string;
  label: string;
  nilai: number | string;
  nilai_sistem: number | null;
  sumber: "sistem" | "diubah" | "rme" | "manual";
  tipe: string;
  standar?: string;
}

export interface RlFormSelView {
  path: string;
  nilai: number;
  nilai_sistem: number;
  sumber: "sistem" | "diubah";
}

export interface RlFormBarisView {
  kunci: string;
  label: string[];
  sel: RlFormSelView[];
}

export interface RlFormSectionView {
  judul: string;
  tipe: "pasangan" | "tabel";
  fields?: RlFormFieldView[];
  kolom_label?: string[];
  kolom_angka?: string[];
  baris?: RlFormBarisView[];
}

export interface RlFormStatus {
  rl: string;
  nama: string;
  periode: PeriodeJenis;
  katalog: boolean;
  jumlah_field: number;
  jumlah_auto: number;
  jumlah_diubah: number;
  tersimpan_pada: string | null;
}

export interface RlFormView {
  periode: Record<string, unknown>;
  rl: string;
  nama: string;
  periode_pelaporan: PeriodeJenis;
  sumber: string;
  katalog: boolean;
  sections: RlFormSectionView[];
  catatan_sistem: string[];
  catatan: string | null;
  jumlah_diubah: number;
  tersimpan_pada: string | null;
}

export interface RlFormSavePayload {
  nilai: Record<string, number>;
  catatan?: string | null;
}

export interface SirsSnapshot {
  id: string;
  rl_form: string;
  status: string;
  result_count: number;
  version: number;
  locked_at: string | null;
  submitted_at: string | null;
}

export interface SirsDashboardResponse {
  periode: Record<string, unknown>;
  dashboard: {
    indikator_rl_3_1: Record<string, number>;
    standar_rl_3_1: Record<string, string>;
    pengunjung_rl_3_4: { baru: number; lama: number; total: number };
    top10_ranap_rl_4_2: Array<Record<string, unknown>>;
    top10_rajal_rl_5_3: Array<Record<string, unknown>>;
  };
  snapshots: SirsSnapshot[];
  rl_forms_fase_1: RlFormMeta[];
  rl_forms_entri: RlFormStatus[];
}

function toQuery(f: SirsPeriodeFilter): string {
  const sp = new URLSearchParams();
  sp.set("periode_jenis", f.periode_jenis);
  sp.set("periode_tahun", String(f.periode_tahun));
  if (f.periode_bulan != null) sp.set("periode_bulan", String(f.periode_bulan));
  if (f.total_tt != null) sp.set("total_tt", String(f.total_tt));
  return sp.toString();
}

async function dashboard(f: SirsPeriodeFilter): Promise<SirsDashboardResponse> {
  const res = await http
    .getDataParam(`/laporan/sirs`, { params: f })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function getRl<T = unknown>(
  rl: string,
  f: SirsPeriodeFilter
): Promise<T> {
  const path = `/laporan/sirs/rl-${rl.replace(".", "-")}`;
  const res = await http
    .getDataParam(path, { params: f })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function listSnapshots(f: SirsPeriodeFilter): Promise<{
  periode: Record<string, unknown>;
  snapshots: SirsSnapshot[];
}> {
  const res = await http
    .getDataParam(`/laporan/sirs/snapshots`, { params: f })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

export interface RlKatalogField {
  key: string;
  no: string;
  label: string;
  auto?: boolean;
  total_of?: string[];
}

export interface RlKatalogForm {
  nama: string;
  periode: string;
  sections: Array<{ judul: string; fields: RlKatalogField[] }>;
}

async function rlFormCatalog(): Promise<{
  forms: Record<string, RlKatalogForm>;
  ringkasan: RlFormStatus[];
}> {
  const res = await http
    .getData(`/laporan/sirs/rl-form/catalog`)
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function rlFormStatus(f: SirsPeriodeFilter): Promise<{
  periode: Record<string, unknown>;
  forms: RlFormStatus[];
}> {
  const res = await http
    .getDataParam(`/laporan/sirs/rl-form/status`, { params: f })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function rlFormShow(
  rl: string,
  f: SirsPeriodeFilter
): Promise<RlFormView> {
  const res = await http
    .getDataParam(`/laporan/sirs/rl-form/${rl}`, { params: f })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function rlFormPrefill(
  rl: string,
  f: SirsPeriodeFilter
): Promise<{ sections: RlFormSectionView[]; jumlah_diubah: number }> {
  const res = await http
    .getDataParam(`/laporan/sirs/rl-form/${rl}/prefill`, { params: f })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function rlFormSave(
  rl: string,
  f: SirsPeriodeFilter,
  payload: RlFormSavePayload
): Promise<{ message: string; jumlah_diubah: number; tersimpan_pada: string | null }> {
  const res = await http
    .putData(`/laporan/sirs/rl-form/${rl}`, {
      periode_jenis: f.periode_jenis,
      periode_tahun: f.periode_tahun,
      periode_bulan: f.periode_bulan,
      ...payload
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

function openXlsxBundle(f: SirsPeriodeFilter): void {
  window.open(`${http.getAPI()}/laporan/sirs/export/xlsx-bundle?${toQuery(f)}`);
}

function openRekapPdf(f: SirsPeriodeFilter): void {
  window.open(`${http.getAPI()}/laporan/sirs/export/rekap-pdf?${toQuery(f)}`);
}

export default {
  dashboard,
  getRl,
  listSnapshots,
  rlFormCatalog,
  rlFormStatus,
  rlFormShow,
  rlFormPrefill,
  rlFormSave,
  openXlsxBundle,
  openRekapPdf
};
